import { useSplitTreatments } from "@splitsoftware/splitio-react";
import { Link } from "@tanstack/react-router";
import { forwardRef, useMemo } from "react";

import type { CommunityScorePercentageType } from "@/components/community-score-wheel";
import GameCategories, {
  type CategoryType,
} from "@/components/game-categories";
import GameListImage from "@/components/game-list-image";
import IconStack, { IconsEnum } from "@/components/icon-stack";
import { CouponsIcon } from "@/components/icon-stack/icons";
import { gameUrl } from "@/helpers/games";
import { useIsBreakPoint } from "@/hooks/use-is-break-point";
import { cn } from "@/lib/utils";
import type { AwardsType } from "@/pages/game/types";

import Properties from "../../../components/properties";
import useGameStatus from "../../../hooks/use-game-status";
import { HaloweenIcon } from "../../../icons";
import type {
  GameKindEnum,
  GameStatusEnum,
  OverallRatingEnum,
} from "../../../types";

export interface LocationCardGameType {
  awards?: AwardsType[];
  closingAt?: string;
  communityRatingCount?: number;
  communityScore?: number;
  communityScoreBucket?: string;
  communityScorePercentages?: CommunityScorePercentageType[];
  coupons?: {
    id: string;
  }[];
  description: string;
  difficulty?: string;
  gameimageSet?: { imageId: string }[];
  id: string;
  isCompleted?: boolean;
  isSaved?: boolean;
  isScary: boolean;
  kind: GameKindEnum;
  minutes?: number;
  name: string;
  openingAt?: string;
  overallRating?: keyof typeof OverallRatingEnum;
  playersMax: number;
  playersMin: number;
  primaryCategory: CategoryType;
  secondaryCategories: CategoryType[];
  slug: string;
  status: keyof typeof GameStatusEnum;
  tags: { id: string }[];
}

interface LocationCardProps extends React.ComponentProps<"div"> {
  className?: string;
  game: LocationCardGameType;
  hasMultipleGames: boolean;
}

export default forwardRef<HTMLDivElement, LocationCardProps>(
  function LocationCard({ className, game, hasMultipleGames, ...props }, ref) {
    const isDesktop = useIsBreakPoint("md");
    const { isOpen, nameSuffix } = useGameStatus(game);
    const { treatments } = useSplitTreatments({
      names: ["xmas_bash_2023", "halloween_party"],
    });

    const hasCoupons = useMemo(() => !!game.coupons?.length, [game.coupons]);
    const treatmentIcons = useMemo(() => {
      const isXmas =
        treatments.xmas_bash_2023?.treatment === "on" &&
        game.tags.some((tag) => tag.id === "christmas");
      const isHalloween =
        treatments.halloween_party?.treatment === "on" &&
        (game.isScary ||
          game.tags.some((tag) =>
            ["spooky", "scary", "very-scary"].includes(tag.id)
          ));

      const icons: React.ReactNode[] = [];

      if (isXmas) {
        icons.push("⛄");
      }
      if (isHalloween) {
        icons.push(<HaloweenIcon className="-mt-1 ml-2.5 inline h-3.5 w-4" />);
      }

      return icons;
    }, [treatments, game]);

    return (
      <div
        className={cn(
          "group relative w-full shrink-0 overflow-hidden rounded-[5px] border border-white/20 bg-white/5 p-[15px] transition-colors hover:bg-white/10",
          hasMultipleGames && "w-9/10",
          className
        )}
        ref={ref}
        {...props}
      >
        <div className="flex items-start gap-[15px]">
          <GameListImage
            className="size-17.5 md:size-25 lg:size-32.5"
            game={game}
          />
          <div className="flex grow flex-col items-stretch gap-[5px] md:gap-2.5">
            <div className="flex items-center gap-[5px]">
              <h2
                className={cn(
                  "grow font-semibold text-14/[1.5em] text-platinum",
                  isOpen && "text-white"
                )}
              >
                <Link
                  className={cn("link-overlay", isOpen && "line-clamp-2")}
                  target={isDesktop ? "_blank" : undefined}
                  to={gameUrl(game)}
                >
                  {game.name}
                  {nameSuffix}
                  {isOpen && hasCoupons && (
                    <CouponsIcon className="ml-2 inline" />
                  )}
                  {treatmentIcons}
                </Link>
              </h2>
              <IconStack
                game={game}
                iconClassName="size-[21px]"
                icons={[IconsEnum.SCORE]}
              />
            </div>
            <div className="hidden md:block">
              <p className="line-clamp-2 text-12/[1.5em] text-pale-lavender">
                {game.description}
              </p>
            </div>
            <Properties className="hidden md:flex" game={game} />
            <GameCategories
              categories={[game.primaryCategory, ...game.secondaryCategories]}
              className="gap-[5px]"
            />
          </div>
        </div>
      </div>
    );
  }
);
